import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileText } from "lucide-react";

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-12 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <div className="inline-block px-3 py-1 text-xs font-semibold bg-primary-100 text-primary-800 rounded-full mb-2">
            Our White Paper Framework
          </div>
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Breaking Down Career Barriers</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            A New Model for Justice Reform
          </p>
          <p className="mt-4 max-w-2xl text-xl text-slate-600 lg:mx-auto">
            Our white paper presents a comprehensive framework that addresses the full continuum of needs for justice-involved individuals.
          </p>
        </div>

        <div className="relative mt-16 pb-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-px h-full bg-primary-200"></div>
          </div>
          
          <div className="relative">
            {/* Phase 1 */}
            <div className="relative z-10 mb-16">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-shrink-0 items-center justify-center w-16 h-16 rounded-full bg-primary-100 border-4 border-white shadow text-primary-600 text-2xl font-bold z-10 md:order-1">
                  1
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md md:w-1/2 md:order-2">
                  <h3 className="text-xl font-bold text-primary-800 mb-2">Skills-First Approach</h3>
                  <p className="text-slate-600 mb-4">
                    Unlike traditional reentry programs that focus primarily on job placement, StaffAbility begins with intensive skills development in high-demand sectors, creating viable career paths rather than just immediate employment.
                  </p>
                  <div className="border-l-4 border-primary-100 pl-4 py-2 bg-primary-50 text-sm text-primary-800 italic">
                    "The key innovation is shifting from 'find a job' to 'build a career' as the core philosophy."
                  </div>
                </div>
                
                <div className="bg-primary-50 p-6 rounded-lg border border-primary-100 md:w-1/2 md:order-3">
                  <h4 className="font-medium text-primary-800 mb-2">Key Elements:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Intensive 8-16 week bootcamp-style training programs</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Industry-recognized certifications and credentials</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Focus on tech, trades, logistics, and healthcare sectors</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Phase 2 */}
            <div className="relative z-10 mb-16">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-shrink-0 items-center justify-center w-16 h-16 rounded-full bg-primary-100 border-4 border-white shadow text-primary-600 text-2xl font-bold z-10 md:order-3">
                  2
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md md:w-1/2 md:order-2">
                  <h3 className="text-xl font-bold text-primary-800 mb-2">Employer Ecosystem</h3>
                  <p className="text-slate-600 mb-4">
                    We're building a network of committed employer partners who recognize the value of second chances and are invested in the long-term success of justice-involved individuals.
                  </p>
                  <div className="border-l-4 border-primary-100 pl-4 py-2 bg-primary-50 text-sm text-primary-800 italic">
                    "Employers are partners in rehabilitation, not just end recipients of placement services."
                  </div>
                </div>
                
                <div className="bg-primary-50 p-6 rounded-lg border border-primary-100 md:w-1/2 md:order-1">
                  <h4 className="font-medium text-primary-800 mb-2">Partnership Framework:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Collaborative curriculum development with industry experts</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Employer tax incentives and community recognition</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Post-placement support and dedicated issue resolution</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Phase 3 */}
            <div className="relative z-10 mb-16">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-shrink-0 items-center justify-center w-16 h-16 rounded-full bg-primary-100 border-4 border-white shadow text-primary-600 text-2xl font-bold z-10 md:order-1">
                  3
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md md:w-1/2 md:order-2">
                  <h3 className="text-xl font-bold text-primary-800 mb-2">24-Month Support Model</h3>
                  <p className="text-slate-600 mb-4">
                    StaffAbility extends support far beyond initial job placement. Our 24-month intensive model provides continuous career development, addressing stability needs throughout the critical reentry period.
                  </p>
                  <div className="border-l-4 border-primary-100 pl-4 py-2 bg-primary-50 text-sm text-primary-800 italic">
                    "Long-term mentorship and support creates resilience against setbacks that typically lead to recidivism."
                  </div>
                </div>
                
                <div className="bg-primary-50 p-6 rounded-lg border border-primary-100 md:w-1/2 md:order-3">
                  <h4 className="font-medium text-primary-800 mb-2">Comprehensive Support:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Housing stability program with transitional options</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Ongoing professional development and upskilling</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Community building and pro-social environment</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Final Element */}
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-shrink-0 items-center justify-center w-16 h-16 rounded-full bg-primary-100 border-4 border-white shadow text-primary-600 text-2xl font-bold z-10 md:order-3">
                  4
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md md:w-1/2 md:order-2">
                  <h3 className="text-xl font-bold text-primary-800 mb-2">Data-Driven Approach</h3>
                  <p className="text-slate-600 mb-4">
                    Our model employs comprehensive metrics beyond recidivism rates, measuring career advancement, economic stability, and long-term outcomes to continuously improve our approach.
                  </p>
                  <div className="border-l-4 border-primary-100 pl-4 py-2 bg-primary-50 text-sm text-primary-800 italic">
                    "What gets measured gets improved - we track holistic success indicators beyond traditional recidivism metrics."
                  </div>
                </div>
                
                <div className="bg-primary-50 p-6 rounded-lg border border-primary-100 md:w-1/2 md:order-1">
                  <h4 className="font-medium text-primary-800 mb-2">Measured Outcomes:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">2-year career trajectory and income growth</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Housing stability and financial independence</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="h-4 w-4 text-primary-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm">Community engagement and prosocial connections</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild className="flex items-center gap-2">
            <Link href="/about">
              <span>Explore Our Vision</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" asChild className="flex items-center gap-2">
            <a href="https://drive.google.com/file/d/1FB4e2OVsvXjox6g4-tFB8YZZledqvrp2/view?usp=sharing" target="_blank" rel="noopener noreferrer">
              <FileText className="h-4 w-4" />
              <span>Download White Paper & Impact Report 2.0</span>
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
